import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import Overline from "@/components/Overline";
import ProductCard from "@/components/ProductCard";
import ProductImage from "@/components/ProductImage";
import { findProduct, findRelated } from "@/data/products";
import { company, seo, buildQuoteUrl } from "@/config/site";
import NotFound from "./NotFound";
import { useParams } from "react-router-dom";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = findProduct(slug);
  if (!product) return <NotFound />;

  const related = findRelated(product.relatedProducts || []);
  const canonical = `${seo.siteUrl}/products/${product.slug}`;
  const quoteUrl = buildQuoteUrl(product.name);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.shortDescription,
    category: product.category,
    brand: { "@type": "Brand", name: company.brandName },
    manufacturer: { "@type": "Organization", name: company.fullCompanyName },
  };

  return (
    <>
      <SEO
        title={product.seoTitle || product.name}
        description={product.seoDescription || product.shortDescription}
        canonical={canonical}
        keywords={product.keywords}
        jsonLd={jsonLd}
      />

      <nav className="border-b border-neutral-200 bg-white" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-neutral-500 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-[#A91D22]">Home</Link>
          <ChevronRight size={12} aria-hidden="true" />
          <Link to="/products" className="hover:text-[#A91D22]">Products</Link>
          <ChevronRight size={12} aria-hidden="true" />
          <span className="text-neutral-900">{product.name}</span>
        </div>
      </nav>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-6">
            <div className="relative aspect-square bg-neutral-100 border border-neutral-200 overflow-hidden" data-testid="product-image">
              <ProductImage product={product} className="absolute inset-0 h-full w-full object-cover" iconSize={140} />
            </div>
            {product.gallery?.length ? (
              <div className="mt-3 grid grid-cols-4 gap-2">
                {product.gallery.slice(0, 4).map((g, i) => (
                  <div key={i} className="aspect-square bg-neutral-100 border border-neutral-200 overflow-hidden">
                    <img src={g} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="lg:col-span-6">
            <Overline>{product.category}</Overline>
            <h1 className="font-heading mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900">{product.name}</h1>
            <p className="mt-5 text-neutral-600 leading-relaxed">{product.fullDescription}</p>

            {product.tags?.length ? (
              <div className="mt-5 flex flex-wrap gap-2">
                {product.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 border border-neutral-300 px-2 py-1">
                    {t}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={quoteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#A91D22] text-white px-6 py-3 text-sm font-medium hover:bg-[#8B181C] rounded-sm"
                data-testid="product-request-quote"
              >
                Request a Quote <ArrowRight size={16} />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 px-6 py-3 text-sm font-medium hover:bg-neutral-50 rounded-sm"
                data-testid="product-talk-team"
              >
                Talk to our team
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 border-y border-neutral-200 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <Overline>Key features</Overline>
            <ul className="mt-4 space-y-3">
              {product.features.map((f) => (
                <li key={f} className="flex gap-3 text-neutral-800">
                  <Check size={18} className="text-[#A91D22] mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Overline>Applications</Overline>
            <ul className="mt-4 space-y-3">
              {product.applications.map((a) => (
                <li key={a} className="flex gap-3 text-neutral-800">
                  <Check size={18} className="text-[#A91D22] mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Overline>Customization</Overline>
          <p className="mt-4 max-w-3xl text-lg text-neutral-800 leading-relaxed">{product.customization}</p>
          {product.specifications?.length ? (
            <div className="mt-8 border border-neutral-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {product.specifications.map((s) => (
                    <tr key={s.label} className="border-b border-neutral-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-neutral-700 w-1/3">{s.label}</td>
                      <td className="px-4 py-3 text-neutral-900">{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-sm text-neutral-500 italic">Specifications can be tailored to your requirement.</p>
          )}
        </div>
      </section>

      {related.length ? (
        <section className="py-14 bg-[#F5F5F5] border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Overline>Related products</Overline>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r) => <ProductCard key={r.slug} product={r} />)}
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-16 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Discuss your {product.name.toLowerCase()} requirement</h2>
          <p className="mt-4 text-neutral-400">Share dimensions, quantity and application. We'll get back with a suitable option.</p>
          <a
            href={quoteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-[#A91D22] text-white px-6 py-3 text-sm font-medium hover:bg-[#8B181C] rounded-sm"
            data-testid="product-final-cta"
          >
            Request a Quote <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </>
  );
}
